import { redirect } from '@tanstack/react-router';
import { setCurrentSite } from './api-handler';
import {
  defaultSiteEnv,
  type HostUser,
  isSiteReady,
  loadedSite,
  loadSessionEnv,
  useEnvStore,
} from './session';

/**
 * The session setup every authenticated page needs: resolve the site and environment the url is
 * for, hold an unprovisioned site at `/getting-ready`, then push a 2FA-pending user to the page
 * where they can enrol. It runs once per navigation from `authedRoute`, so the routes beneath only
 * carry what is specific to them.
 *
 * A logged-in visitor to a *public* page goes through it too (`publicRoute`).
 */

/** The only two environments a site has. */
const SITE_ENVS = ['live', 'test'];

const GETTING_READY_PATH = '/getting-ready';

/**
 * The 2FA redirect skips these three because they are the other guards' own destinations: without
 * the exemption each guard redirects away from the other's target and the navigation loops.
 */
const TWO_FACTOR_EXEMPT_PATHS = ['/logout', GETTING_READY_PATH, '/no-team'];

type GuardLocation = { pathname: string; searchStr: string; hash: string };

type GuardMatch = { staticData?: { staffOnly?: boolean; unknownSitePage?: boolean } };

export type GuardContext = {
  user: HostUser;
  params: { siteId?: string; siteEnv?: string };
  location: GuardLocation;
  matches: ReadonlyArray<GuardMatch>;
};

/** Whether the matched route is staff-only. Checked before any site loads. */
export function isStaffOnly(matches: ReadonlyArray<GuardMatch>): boolean {
  return matches.some((match) => match.staticData?.staffOnly);
}

/** Whether the url named a site but a page under it that does not exist. */
function isUnknownSitePage(matches: ReadonlyArray<GuardMatch>): boolean {
  return matches.some((match) => match.staticData?.unknownSitePage);
}

/** The `?search#hash` half of a location, for redirects that rebuild only the pathname. */
export function locationSuffix(location: { searchStr: string; hash: string }): string {
  return `${location.searchStr}${location.hash ? `#${location.hash}` : ''}`;
}

/** The current site url under another environment, keeping the leaf path, search and hash. */
function withSiteEnv(location: GuardLocation, siteId: string, siteEnv: string): string {
  const [, , , ...rest] = location.pathname.split('/');
  const tail = rest.length > 0 ? `/${rest.join('/')}` : '';
  return `/${siteId}/${siteEnv}${tail}${locationSuffix(location)}`;
}

/**
 * The environment to load for a site the url named without one. The environment already loaded for
 * this site wins - moving from `/$siteId/test` to `/$siteId/settings` must not drop back to live -
 * then the site's own default (partners have no live environment).
 */
function envForSite(user: HostUser, siteId: string): string {
  const { siteId: loadedSiteId, siteEnv: loadedSiteEnv } = useEnvStore.getState();
  const sameSite = loadedSiteId === siteId ? loadedSiteEnv : null;
  return sameSite ?? defaultSiteEnv(user, siteId) ?? 'live';
}

/** Load the site/environment this url is for. */
async function resolveEnv({ user, params, location, matches }: GuardContext): Promise<void> {
  const { siteId, siteEnv: urlEnv } = params;

  if (!siteId) {
    // The url names no site, so keep whatever the session already holds and only set the user's
    // own team up when nothing is loaded. A failed load holds a site but is not loaded.
    if (!loadedSite() && user.currentTeam) {
      const slug = user.currentTeam.slugged_name;
      await loadSessionEnv(slug, defaultSiteEnv(user, slug) ?? 'live', 'any');
    }
    return;
  }

  const urlEnvIsReal = urlEnv !== undefined && SITE_ENVS.includes(urlEnv);
  const unknownPage = isUnknownSitePage(matches);

  if (urlEnv !== undefined && (!urlEnvIsReal || unknownPage)) {
    // A url the router could only half make sense of: the environment segment is not an
    // environment (`/acme/staging/...`), or there is no such page under the site. The site itself
    // may well be real, so look it up quietly - a mistyped url should not raise an error toast -
    // and keep the user on it rather than on whichever team they happen to have attached.
    const probe = await loadSessionEnv(
      siteId,
      urlEnvIsReal ? urlEnv : envForSite(user, siteId),
      'any',
      { quiet: true },
    );
    if (!probe || unknownPage) {
      // No such site, or no such page on it. `/` resolves the landing page, which prefers the site
      // just loaded over the user's own team - so a real site keeps the user, and a junk one
      // quietly lands them back home.
      throw redirect({ to: '/' });
    }
    throw redirect({ href: withSiteEnv(location, siteId, probe.environment) });
  }

  // An environment we picked ourselves can move anywhere that loads. One the url asked for may
  // only move for a partner site, which has no live environment to ask for.
  const env = await loadSessionEnv(
    siteId,
    urlEnv ?? envForSite(user, siteId),
    urlEnv === undefined ? 'any' : 'partner',
  );
  // A refused or failed load is the getting-ready gate's to answer for, not this block's.
  if (!env) return;

  // Pages beneath call the proxy API, which is addressed by the site/env actually loaded - for a
  // partner the corrected `test` env, not the `live` the url asked for.
  setCurrentSite({ siteId, siteEnv: env.environment });

  if (urlEnv !== undefined && env.environment !== urlEnv) {
    // A partner site asked for its live environment loads test instead. Send the whole url after
    // it - the matched leaf, its search and its hash - rather than dropping to the site index.
    throw redirect({ href: withSiteEnv(location, siteId, env.environment) });
  }
}

/** Env resolution, then the getting-ready gate, then the 2FA redirect. */
export async function runSessionGuards(context: GuardContext): Promise<void> {
  const { user, location } = context;

  await resolveEnv(context);

  const env = useEnvStore.getState().env;

  // A site whose database is still being provisioned has nothing for any page to read.
  if (location.pathname !== GETTING_READY_PATH && user.currentTeam && !isSiteReady(env)) {
    throw redirect({ to: GETTING_READY_PATH });
  }

  // Staff whose 2FA is required but not yet enrolled get pushed to the password settings page,
  // where two-factor setup lives. UX only - the server middleware is the real enforcement.
  const twoFactorPending =
    user.twoFactor.required &&
    !user.twoFactor.enabled &&
    !TWO_FACTOR_EXEMPT_PATHS.includes(location.pathname) &&
    !location.pathname.endsWith('/settings/password');

  if (twoFactorPending) {
    // The site just loaded for this url, else the user's own team - `settings/password` needs a
    // `siteId`, and with neither there is nothing to pass, so leave the navigation alone (the
    // server still enforces).
    const siteId = env?.team.slugged_name ?? user.currentTeam?.slugged_name;
    if (siteId) {
      throw redirect({ to: '/$siteId/settings/password', params: { siteId } });
    }
  }
}
